import FVector from '@data/geometry/FVector';
import CustomCamera, { CameraSettings } from './CustomCamera';
import { Vector3, Spherical } from 'three';

export class CameraTOP extends CustomCamera {
    private _returnPhi: number | null = null;

    public setReturnFromPose(position: FVector, target: FVector): void {
        const P = new Vector3(position.x, position.y, position.z);
        const T = new Vector3(target.x, target.y, target.z);
        const v = P.clone().sub(T);

        if (v.lengthSq() > 1e-12) {
            const sph = new Spherical().setFromVector3(v);
            this._returnPhi = sph.phi;
        } else {
            this._returnPhi = null;
        }
    }

    public override activate(position: FVector, target: FVector): void {
        // If helper didn't set it, derive it from incoming pose
        if (this._returnPhi === null) {
            this.setReturnFromPose(position, target);
        }
        super.activate(position, target);
    }

    public override deActivate(): { position: FVector; target: FVector } {
        // Keep CURRENT target
        const T = this.orbitControls.target.clone();

        // If we don't have a stored phi, just return current pose
        if (this._returnPhi === null) {
            return super.deActivate();
        }

        // Get CURRENT TOP offset
        const Pcur = this.orbitControls.object.position.clone();
        const vCur = Pcur.clone().sub(T);

        // Convert to spherical, then restore ONLY phi
        const sphCur = new Spherical().setFromVector3(vCur);
        sphCur.phi = this._returnPhi;

        // Rebuild position using current radius/theta, restored phi
        const vOut = new Vector3().setFromSpherical(sphCur);
        const Pout = T.clone().add(vOut);

        return {
            position: new FVector(Pout.x, Pout.y, Pout.z),
            target: new FVector(T.x, T.y, T.z),
        };
    }

    public override setPositionAndTarget(position: FVector, target: FVector): void {
        const P = new Vector3(position.x, position.y, position.z);
        const T = new Vector3(target.x, target.y, target.z);

        const v = P.clone().sub(T);
        const len = v.length();

        if (len === 0) {
            const worldUp = new Vector3(0, 1, 0);
            const fallbackPos = T.clone()
                .addScaledVector(worldUp, 1)
                .addScaledVector(new Vector3(1, 0, 0), 1e-2);

            this.orbitControls.object.position.copy(fallbackPos);
            this.orbitControls.target.copy(T);
            this.orbitControls.update();
            return;
        }

        const worldUp = new Vector3(0, 1, 0);

        // Preserve heading from incoming pose
        const vHoriz = v.clone().projectOnPlane(worldUp);
        const minHoriz = Math.max(1e-2, len * 1e-4); // enough to survive rounding/JSON

        let horizDir: Vector3;
        if (vHoriz.lengthSq() > 1e-12) {
            horizDir = vHoriz.clone().normalize();
        } else {
            horizDir = new Vector3(1, 0, 0);
        }

        // Force a non-zero horizontal component (avoids top singularity)
        const Ptop = T.clone().addScaledVector(worldUp, len).addScaledVector(horizDir, minHoriz);

        this.orbitControls.object.position.copy(Ptop);
        this.orbitControls.target.copy(T);
        this.orbitControls.update();
    }

    public override getPositionAndTarget(): { position: FVector; target: FVector } {
        const P = this.orbitControls.object.position;
        const T = this.orbitControls.target;

        return {
            position: new FVector(P.x, P.y, P.z),
            target: new FVector(T.x, T.y, T.z),
        };
    }

    protected override defaultSettings(): CameraSettings {
        return {
            ...super.defaultSettings(),
            enableZoom: true,
            maxPolarAngle: 0.001,
            trackBallZoom: false,
        };
    }
}
